import React from 'react';
import {
  BarChart3,
  Bell,
  LayoutDashboard,
  LogIn,
  Package,
  Smartphone,
  Store,
  TrendingUp
} from 'lucide-react';

export function MobileAppsHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-500/[0.08] via-transparent to-indigo-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* App screen */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,250px)] rounded-[1.75rem] border border-white/[0.15] bg-slate-950/90 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(56,189,248,0.3)] p-2 z-20 rotate-[-3deg] hover:rotate-0 transition-transform duration-500">
        <div className="mx-auto w-10 h-1 rounded-full bg-white/20 mb-2" />
        <div className="rounded-[1.25rem] bg-gradient-to-b from-sky-500/15 to-slate-900/95 p-3 border border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <Smartphone className="w-4 h-4 text-sky-400" aria-hidden />
            <span className="text-[9px] font-semibold text-white/55">{mock.appLabel}</span>
          </div>
          <div className="h-20 rounded-xl bg-white/[0.06] border border-white/[0.05] mb-2" />
          <div className="grid grid-cols-2 gap-1.5">
            <div className="h-10 rounded-lg bg-sky-500/20 border border-sky-500/25" />
            <div className="h-10 rounded-lg bg-white/[0.04]" />
          </div>
        </div>
      </div>

      {/* Login */}
      <div className="absolute right-0 top-0 w-[min(100%,220px)] rounded-[1.5rem] border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(99,102,241,0.3)] p-2 z-10 rotate-[3deg] sm:right-2 lg:right-8 hover:rotate-0 transition-transform duration-500">
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-indigo-500/10 to-slate-900/80 p-3">
          <LogIn className="w-4 h-4 text-indigo-400 mb-2" aria-hidden />
          <span className="text-[9px] font-semibold text-white/65 block mb-2">{mock.loginLabel}</span>
          <div className="space-y-1.5 mb-2">
            <div className="h-5 rounded-md bg-white/[0.06] border border-white/[0.05]" />
            <div className="h-5 rounded-md bg-white/[0.04] border border-white/[0.04]" />
          </div>
          <div className="h-6 rounded-md bg-indigo-500/30 border border-indigo-500/35" />
        </div>
      </div>

      {/* Push notifications */}
      <div className="absolute left-10 sm:left-20 bottom-32 sm:bottom-36 w-[160px] rounded-xl border border-amber-500/25 bg-amber-950/50 backdrop-blur-md px-3 py-2 z-30 shadow-lg rotate-[2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-400 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p className="text-[9px] font-bold text-amber-200/95 truncate">{mock.pushLabel}</p>
            <p className="text-[8px] text-amber-200/50 truncate">{mock.pushDesc}</p>
          </div>
        </div>
      </div>

      {/* Dashboard */}
      <div className="absolute right-6 sm:right-12 bottom-24 sm:bottom-28 w-[min(100%,200px)] rounded-xl border border-emerald-500/25 bg-emerald-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <LayoutDashboard className="w-4 h-4 text-emerald-400" aria-hidden />
          <span className="text-[10px] font-bold text-emerald-200/95">{mock.dashboardLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {[40, 55, 35].map((h, i) => (
            <div key={i} className="rounded bg-emerald-500/20 border border-emerald-500/15 flex items-end p-0.5" style={{ height: `${h}px` }}>
              <div className="w-full h-1 rounded-full bg-emerald-400/40" />
            </div>
          ))}
        </div>
      </div>

      {/* Store badges */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-white/[0.1] bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(56,189,248,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-200/80">{mock.storesLabel}</span>
          <Store className="w-3.5 h-3.5 text-sky-400" aria-hidden />
        </div>
        <div className="flex gap-2">
          <div className="flex-1 h-8 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center">
            <span className="text-[8px] font-bold text-white/50">{mock.playStore}</span>
          </div>
          <div className="flex-1 h-8 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center">
            <span className="text-[8px] font-bold text-white/50">{mock.appStore}</span>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/[0.08]">
          <Package className="w-3.5 h-3.5 text-sky-400" aria-hidden />
          <span className="text-[9px] text-sky-300 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" aria-hidden />
            <BarChart3 className="w-3 h-3" aria-hidden />
            {mock.metric}
          </span>
        </div>
      </div>
    </div>
  );
}
